import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Animated,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import LottieConfetti from "@/components/LottieConfetti";
import { Ionicons } from "@expo/vector-icons";

const FEATURES = [
  "Class action claims",
  "Unclaimed money search",
  "AI Refund Assistant",
  "Money tracking",
];

const BudgetSuccessScreen = () => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;

  useEffect(() => {
    // Fade in animation
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 800,
      useNativeDriver: true,
    }).start();

    // Scale animation for party emoji
    Animated.spring(scaleAnim, {
      toValue: 1,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim, scaleAnim]);

  const handleViewClaims = () => {
    router.dismissTo("/(tabs)/feeds");
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.content}>
          {/* Party Emoji */}
          <Animated.View
            style={[
              styles.emojiContainer,
              {
                opacity: fadeAnim,
                transform: [{ scale: scaleAnim }],
              },
            ]}
          >
            <Text style={styles.partyEmoji}>🎉</Text>
          </Animated.View>

          {/* Success Text */}
          <Animated.View style={[styles.textContainer, { opacity: fadeAnim }]}>
            <Text style={styles.title}>You're all set!</Text>
            <Text style={styles.subtitle}>
              Here's how Sparrow AI{"\n"}will help you manage money:
            </Text>
          </Animated.View>

          {/* Features List */}
          <Animated.View style={[styles.featuresList, { opacity: fadeAnim }]}>
            {FEATURES.map((feature, index) => (
              <View key={index} style={styles.featureRow}>
                <Ionicons name="checkmark" size={20} color="#0F9129" />
                <Text style={styles.featureText}>{feature}</Text>
              </View>
            ))}
          </Animated.View>
        </View>

        {/* Bottom Button */}
        <Animated.View style={[styles.bottomContainer, { opacity: fadeAnim }]}>
          <ActionButton title="View My Claims" onPress={handleViewClaims} />
        </Animated.View>

        {/* Confetti */}
        <LottieConfetti active={true} />
      </SafeAreaView>
    </View>
  );
};

export default BudgetSuccessScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  emojiContainer: {
    marginBottom: 24,
  },
  partyEmoji: {
    fontSize: 64,
  },
  textContainer: {
    alignItems: "center",
    marginBottom: 32,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    color: "#161D14",
    textAlign: "center",
    marginBottom: 16,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "#161D14",
    textAlign: "center",
    opacity: 0.7,
  },
  featuresList: {
    alignSelf: "flex-start",
    gap: 16,
    paddingLeft: 16,
  },
  featureRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  featureText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 16,
    color: "#161D14",
  },
  bottomContainer: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
});
